import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Autocomplete,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack
} from '@mui/material';
import { Person, Business, Preview, Check } from '@mui/icons-material';
import { useMutation, useQuery } from 'react-query';
import client from '../../../../../API';
import { toast } from 'sonner';

const RetroactiveReferralDialog = ({ open, onClose, onSuccess }) => {
  // =================== STATE ===================
  const [activeStep, setActiveStep] = useState(0);
  const [referringUser, setReferringUser] = useState(null);
  const [referredUser, setReferredUser] = useState(null);
  const [referralType, setReferralType] = useState(null);
  const [referralData, setReferralData] = useState({});
  const [referringSearch, setReferringSearch] = useState('');
  const [referredSearch, setReferredSearch] = useState('');

  const isSubmittingRef = useRef(false);

  const steps = ['Select Users', 'Preview Data', 'Confirm Assignment'];

  // =================== API QUERIES ===================
  const { data: referringUsersData, isLoading: referringUsersLoading } = useQuery(
    ['users-search-referring', referringSearch],
    async () => {
      if (!referringSearch || referringSearch.length < 2) return [];
      const response = await client.referrals.searchUsers(referringSearch);
      return response.users || [];
    },
    { enabled: referringSearch.length >= 2, staleTime: 30000 }
  );

  const { data: referredUsersData, isLoading: referredUsersLoading } = useQuery(
    ['users-search-referred', referredSearch],
    async () => {
      if (!referredSearch || referredSearch.length < 2) return [];
      const response = await client.referrals.searchUsers(referredSearch);
      return response.users || [];
    },
    { enabled: referredSearch.length >= 2, staleTime: 30000 }
  );

  const { data: referredUserDetails, isLoading: detailsLoading } = useQuery(
    ['user-details', referredUser?.id],
    async () => {
      if (!referredUser) return null;
      const response = await client.referrals.getUserDetails(referredUser.id);
      return response.user;
    },
    { enabled: !!referredUser?.id && activeStep === 1 }
  );

  // =================== EFFECTS ===================
  useEffect(() => {
    if (referredUserDetails && !referralType) {
      const userType = referredUserDetails.user_type;
      setReferralType(userType === 'creator' ? 'creator' : 'company');
      buildReferralData(referredUserDetails);
    }
  }, [referredUserDetails, referralType]);

  useEffect(() => {
    isSubmittingRef.current = false;
  }, [activeStep]);

  // =================== HELPER FUNCTIONS ===================
  const buildReferralData = (details) => {
    if (!details) return;

    // FIX: robust name fallbacks so required fields never land as empty string
    const firstName =
      details.first_name ||
      details.display_name?.split(' ')[0] ||
      details.username ||
      '';

    const lastName =
      details.last_name ||
      (details.display_name?.split(' ').length > 1
        ? details.display_name.split(' ').slice(1).join(' ')
        : '') ||
      '';

    const baseData = {
      firstName,
      lastName,
      email: details.email || '',
      phoneNumber: details.phone_number || '',
    };

    if (details.user_type === 'creator' || details.creator_username) {
      setReferralData({
        ...baseData,
        referralType: 'creator',
        creatorUsername: details.creator_username || details.username || '',
        primaryPlatform: details.primary_platform || 'instagram',
        // FIX: contentNiche must not be empty — fallback to 'General'
        contentNiche: details.category || details.content_niche || 'General',
        followerCount: details.follower_count || details.followers || '',
        avgEngagementRate: details.engagement_rate || '',
        portfolioUrl: details.portfolio_url || '',
        bio: details.bio || '',
      });
    } else {
      setReferralData({
        ...baseData,
        referralType: 'company',
        // FIX: companyName fallback so it's never empty
        companyName: details.company_name || details.username || '',
        // FIX: industry fallback so it's never empty
        industry: details.industry || 'Marketing',
        country: details.country || 'United States',
        bio: details.bio || '',
        businessType: details.business_type || 'brand',
      });
    }
  };

  // =================== STEP 1 VALIDATION ===================
  const validateStep1Fields = () => {
    const requiredFields =
      referralType === 'creator'
        ? [
            { key: 'firstName', label: 'First Name' },
            { key: 'email', label: 'Email' },
            { key: 'primaryPlatform', label: 'Primary Platform' },
            { key: 'contentNiche', label: 'Content Niche' },
          ]
        : [
            { key: 'firstName', label: 'First Name' },
            { key: 'email', label: 'Email' },
            { key: 'companyName', label: 'Company Name' },
            { key: 'industry', label: 'Industry' },
          ];

    for (const { key, label } of requiredFields) {
      if (!referralData[key]?.toString().trim()) {
        toast.error(`${label} is required — please fill it in before continuing`);
        return false;
      }
    }
    return true;
  };

  // =================== MUTATIONS ===================
  const createReferralMutation = useMutation(
    (data) => client.referrals.create(data),
    {
      onSuccess: () => {
        toast.success('Referral created successfully!');
        onSuccess?.();
        handleClose();
      },
      onError: (error) => {
        toast.error(error?.response?.data?.error || error?.message || 'Failed to create referral');
      },
    }
  );

  const validateMutation = useMutation(
    (data) => client.referrals.validateAssignment(data),
    {
      onSuccess: (data) => {
        isSubmittingRef.current = false;
        if (data.valid) {
          setActiveStep(2);
        } else {
          toast.error(data.error || 'Assignment validation failed');
        }
      },
      onError: (error) => {
        isSubmittingRef.current = false;
        toast.error(error?.response?.data?.error || 'Validation failed');
      },
    }
  );

  // =================== HANDLERS ===================
  const handleNext = () => {
    if (activeStep === 0) {
      if (!referringUser || !referredUser) {
        toast.error('Please select both users');
        return;
      }
      if (!referringUser.id) {
        toast.error('Referring user is missing an ID — please re-select');
        return;
      }
      if (!referredUser.id) {
        toast.error('Referred user is missing an ID — please re-select');
        return;
      }
      setActiveStep(1);
    } else if (activeStep === 1) {
      if (isSubmittingRef.current || validateMutation.isLoading) return;

      if (!referringUser?.id || !referredUser?.id) {
        toast.error('User selection was lost — please go back and re-select');
        return;
      }

      // FIX: validate required referral fields before firing the API call
      if (!validateStep1Fields()) return;

      isSubmittingRef.current = true;
      validateMutation.mutate({
        referringUserId: referringUser.id,
        referredUserId: referredUser.id,
      });
    }
  };

  const handleBack = () => {
    isSubmittingRef.current = false;
    setActiveStep((prev) => prev - 1);
  };

  const handleClose = () => {
    isSubmittingRef.current = false;
    setActiveStep(0);
    setReferringUser(null);
    setReferredUser(null);
    setReferralType(null);
    setReferralData({});
    setReferringSearch('');
    setReferredSearch('');
    onClose();
  };

  const handleSubmit = () => {
    const payload = {
      ...referralData,
      referringUserId: referringUser.id,
      endUserId: referredUser.id,
      referralSource: 'retroactive_assignment',
      status: 'completed',
      notes: `Retroactively assigned referral from ${referringUser.username} to ${referredUser.username}`,
    };

    createReferralMutation.mutate(payload);
  };

  // =================== SHARED OPTION RENDERER ===================
  const renderUserOption = (props, option) => (
    <li {...props} key={option.id}>
      <Box display="flex" alignItems="center" width="100%">
        {option.type === 'creator' ? (
          <Person sx={{ mr: 1, color: 'primary.main' }} />
        ) : (
          <Business sx={{ mr: 1, color: 'secondary.main' }} />
        )}
        <Box flex={1}>
          <Typography variant="body2" fontWeight="bold">
            {option.display_name || option.username}
          </Typography>
          <Typography variant="caption" color="textSecondary">
            {option.email} • {option.type === 'creator' ? option.platform : option.company}
          </Typography>
        </Box>
        <Chip
          label={option.type}
          size="small"
          color={option.type === 'creator' ? 'primary' : 'secondary'}
        />
      </Box>
    </li>
  );

  // =================== RENDER STEPS ===================
  const renderStep0 = () => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom>
        Select Referring User
      </Typography>
      <Autocomplete
        options={referringUsersData || []}
        getOptionLabel={(option) =>
          option ? `${option.display_name || option.username} (${option.email})` : ''
        }
        isOptionEqualToValue={(option, value) => option?.id === value?.id}
        loading={referringUsersLoading}
        value={referringUser}
        onChange={(_, newValue) => {
          setReferringUser(newValue);
          if (newValue && referredUser?.id === newValue.id) {
            setReferredUser(null);
          }
        }}
        onInputChange={(_, newValue, reason) => {
          if (reason === 'input') setReferringSearch(newValue);
          if (reason === 'clear') setReferringUser(null);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Search for referring user..."
            placeholder="Type username, email, or name"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {referringUsersLoading ? <CircularProgress size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
        renderOption={renderUserOption}
        sx={{ mb: 3 }}
      />

      {referringUser && (
        <>
          <Divider sx={{ my: 3 }} />
          <Typography variant="h6" gutterBottom>
            Select Referred User
          </Typography>
          <Autocomplete
            options={(referredUsersData || []).filter((u) => u.id !== referringUser?.id)}
            getOptionLabel={(option) =>
              option ? `${option.display_name || option.username} (${option.email})` : ''
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            loading={referredUsersLoading}
            value={referredUser}
            onChange={(_, newValue) => setReferredUser(newValue)}
            onInputChange={(_, newValue, reason) => {
              if (reason === 'input') setReferredSearch(newValue);
              if (reason === 'clear') setReferredUser(null);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Search for referred user..."
                placeholder="Type username, email, or name"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {referredUsersLoading ? <CircularProgress size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={renderUserOption}
          />
        </>
      )}

      {referringUser && referredUser && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            <strong>{referringUser.display_name || referringUser.username}</strong> will be marked as
            referring <strong>{referredUser.display_name || referredUser.username}</strong>
          </Typography>
        </Alert>
      )}
    </Box>
  );

  const renderStep1 = () => (
    <Box sx={{ mt: 3 }}>
      {detailsLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Alert severity="info" sx={{ mb: 3 }}>
            Review the auto-populated data below. Fields marked with * are required — fill any that
            are blank before continuing.
          </Alert>

          <Card elevation={2} sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Referral Type: {referralType === 'creator' ? 'Creator' : 'Company'}
              </Typography>
              <Grid container spacing={2} sx={{ mt: 1 }}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    required
                    label="First Name"
                    value={referralData.firstName || ''}
                    onChange={(e) => setReferralData({ ...referralData, firstName: e.target.value })}
                    error={!referralData.firstName?.trim()}
                    helperText={!referralData.firstName?.trim() ? 'Required' : ''}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={referralData.lastName || ''}
                    onChange={(e) => setReferralData({ ...referralData, lastName: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    label="Email"
                    value={referralData.email || ''}
                    onChange={(e) => setReferralData({ ...referralData, email: e.target.value })}
                    error={!referralData.email?.trim()}
                    helperText={!referralData.email?.trim() ? 'Required' : ''}
                  />
                </Grid>

                {referralType === 'creator' ? (
                  <>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Creator Username"
                        value={referralData.creatorUsername || ''}
                        onChange={(e) =>
                          setReferralData({ ...referralData, creatorUsername: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth required>
                        <InputLabel>Primary Platform</InputLabel>
                        <Select
                          value={referralData.primaryPlatform || 'instagram'}
                          onChange={(e) =>
                            setReferralData({ ...referralData, primaryPlatform: e.target.value })
                          }
                          label="Primary Platform"
                        >
                          <MenuItem value="instagram">Instagram</MenuItem>
                          <MenuItem value="tiktok">TikTok</MenuItem>
                          <MenuItem value="youtube">YouTube</MenuItem>
                          <MenuItem value="twitter">Twitter/X</MenuItem>
                          <MenuItem value="twitch">Twitch</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Content Niche"
                        value={referralData.contentNiche || ''}
                        onChange={(e) =>
                          setReferralData({ ...referralData, contentNiche: e.target.value })
                        }
                        error={!referralData.contentNiche?.trim()}
                        helperText={!referralData.contentNiche?.trim() ? 'Required' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Follower Count"
                        value={referralData.followerCount || ''}
                        onChange={(e) =>
                          setReferralData({ ...referralData, followerCount: e.target.value })
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Engagement Rate"
                        value={referralData.avgEngagementRate || ''}
                        onChange={(e) =>
                          setReferralData({ ...referralData, avgEngagementRate: e.target.value })
                        }
                      />
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        required
                        label="Company Name"
                        value={referralData.companyName || ''}
                        onChange={(e) =>
                          setReferralData({ ...referralData, companyName: e.target.value })
                        }
                        error={!referralData.companyName?.trim()}
                        helperText={!referralData.companyName?.trim() ? 'Required' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        required
                        label="Industry"
                        value={referralData.industry || ''}
                        onChange={(e) =>
                          setReferralData({ ...referralData, industry: e.target.value })
                        }
                        error={!referralData.industry?.trim()}
                        helperText={!referralData.industry?.trim() ? 'Required' : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth>
                        <InputLabel>Business Type</InputLabel>
                        <Select
                          value={referralData.businessType || 'brand'}
                          onChange={(e) =>
                            setReferralData({ ...referralData, businessType: e.target.value })
                          }
                          label="Business Type"
                        >
                          <MenuItem value="brand">Brand</MenuItem>
                          <MenuItem value="agency">Agency</MenuItem>
                          <MenuItem value="other">Other</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Phone Number"
                    value={referralData.phoneNumber || ''}
                    onChange={(e) =>
                      setReferralData({ ...referralData, phoneNumber: e.target.value })
                    }
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );

  const renderStep2 = () => (
    <Box sx={{ mt: 3 }}>
      <Alert severity="success" sx={{ mb: 3 }}>
        <Typography variant="body2" fontWeight="bold">
          Ready to create referral!
        </Typography>
      </Alert>

      <Card elevation={2}>
        <CardContent>
          <Stack spacing={2}>
            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Referring User
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {referringUser?.display_name || referringUser?.username}
              </Typography>
              <Typography variant="caption">{referringUser?.email}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="textSecondary">
                Referred User ({referralType === 'creator' ? 'Creator' : 'Company'})
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {referredUser?.display_name || referredUser?.username}
              </Typography>
              <Typography variant="caption">{referredUser?.email}</Typography>
            </Box>

            <Divider />

            <Box>
              <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                Referral Details
              </Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Name:
                  </Typography>
                  <Typography variant="body2">
                    {referralData.firstName} {referralData.lastName}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary">
                    Email:
                  </Typography>
                  <Typography variant="body2">{referralData.email}</Typography>
                </Grid>
                {referralType === 'creator' ? (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Platform:
                      </Typography>
                      <Typography variant="body2">{referralData.primaryPlatform}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Niche:
                      </Typography>
                      <Typography variant="body2">{referralData.contentNiche}</Typography>
                    </Grid>
                  </>
                ) : (
                  <>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Company:
                      </Typography>
                      <Typography variant="body2">{referralData.companyName}</Typography>
                    </Grid>
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        Industry:
                      </Typography>
                      <Typography variant="body2">{referralData.industry}</Typography>
                    </Grid>
                  </>
                )}
              </Grid>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );

  // =================== MAIN RENDER ===================
  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center">
          <Preview sx={{ mr: 1 }} />
          Retroactive Referral Assignment
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === 0 && renderStep0()}
        {activeStep === 1 && renderStep1()}
        {activeStep === 2 && renderStep2()}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        {activeStep > 0 && (
          <Button onClick={handleBack} disabled={validateMutation.isLoading}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button
            variant="contained"
            onClick={handleNext}
            disabled={
              (activeStep === 0 && (!referringUser || !referredUser)) ||
              validateMutation.isLoading ||
              isSubmittingRef.current ||
              (activeStep === 1 && detailsLoading)
            }
          >
            {activeStep === 1 && validateMutation.isLoading ? (
              <>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                Validating...
              </>
            ) : (
              'Next'
            )}
          </Button>
        ) : (
          <Button
            variant="contained"
            color="success"
            onClick={handleSubmit}
            disabled={createReferralMutation.isLoading}
            startIcon={
              createReferralMutation.isLoading ? <CircularProgress size={20} /> : <Check />
            }
          >
            {createReferralMutation.isLoading ? 'Creating...' : 'Create Referral'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default RetroactiveReferralDialog;